import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import VideoPlayer from "@/pages/detail/components/VideoPlayer";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatHeat, getAnimeById, getRelatedAnime, categoryIdFromName } from "@/utils/anime";

export default function DetailPage() {
  const { animeId = "" } = useParams();
  const anime = getAnimeById(animeId);
  const [episode, setEpisode] = useState(1);
  const [toast, setToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();

  const related = useMemo(() => (anime ? getRelatedAnime(anime, 4) : []), [anime]);

  useDocumentMeta(
    anime ? `${anime.title} - 在线观看 - 动漫免费在观看` : "动漫详情 - 动漫免费在观看",
    anime
      ? `${anime.summary}《${anime.title}》共 ${anime.episodes} 集，${anime.region}${anime.year}年${anime.category}动漫，${anime.status}中，尽在动漫免费在观看。`
      : "查看动漫详情并在线观看高清动漫作品。",
    anime ? `${anime.title},${anime.title}在线观看,${anime.category}动漫,免费动漫,动漫免费在观看` : undefined,
  );

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleFavorite = () => {
    if (!anime) return;
    const added = toggleFavorite(anime.id);
    showToast(added ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </div>
        <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">未找到该动漫</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的作品不存在或已下架。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(anime.id);
  const episodeLabel = `第 ${episode} 集`;

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors flex items-center gap-1">
          <i className="ri-home-4-line"></i>
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${categoryIdFromName(anime.category)}`} className="hover:text-primary-600 transition-colors">
          {anime.category}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 font-medium truncate max-w-[200px]">{anime.title}</span>
      </nav>

      <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-5 md:gap-7">
          <div className="w-full md:w-[220px] lg:w-[240px] shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
              <img
                src={anime.cover}
                alt={anime.title}
                title={`${anime.title} - 在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              {anime.title}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">{anime.status}</span>
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">{anime.category}</span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{anime.region}</span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{anime.year} 年</span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">共 {anime.episodes} 集</span>
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium flex items-center gap-1">
                <i className="ri-star-fill"></i>
                {anime.rating}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium flex items-center gap-1">
                <i className="ri-fire-fill"></i>
                {formatHeat(anime.heat)} 热度
              </span>
            </div>

            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              {anime.summary}
            </p>

            <div className="flex flex-wrap gap-2 mt-4">
              {anime.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs">
                  # {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#player"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                开始观看
              </a>
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-share-forward-line text-lg"></i>
                分享
              </button>
            </div>
          </div>
        </div>
      </div>

      <div id="player" className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 lg:gap-8 mt-6 lg:mt-8 items-start">
        <div className="space-y-6">
          <VideoPlayer poster={anime.banner} title={anime.title} episodeLabel={episodeLabel} />
          <EpisodeList total={anime.episodes} current={episode} onChange={setEpisode} />
        </div>

        <aside className="space-y-6">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h2 className="font-heading font-bold text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-file-list-3-line text-primary-500"></i>
              基本信息
            </h2>
            <dl className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">类型</dt>
                <dd className="text-foreground-900 text-right">{anime.category}</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">地区</dt>
                <dd className="text-foreground-900 text-right">{anime.region}</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">年份</dt>
                <dd className="text-foreground-900 text-right">{anime.year} 年</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">状态</dt>
                <dd className="text-foreground-900 text-right">{anime.status}</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">集数</dt>
                <dd className="text-foreground-900 text-right">{anime.episodes} 集</dd>
              </div>
              <div className="flex items-start justify-between gap-3">
                <dt className="text-foreground-500 shrink-0">更新时间</dt>
                <dd className="text-foreground-900 text-right">{anime.updatedAt}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
            <h2 className="font-heading font-bold text-foreground-950 flex items-center gap-2 mb-3">
              <i className="ri-information-line text-secondary-600"></i>
              演示说明
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              本页播放器为前端演示控件，仅模拟播放、暂停与进度条交互，不接入真实视频流，也不会展示虚假的播放成功反馈。收藏记录保存在你的浏览器本地，刷新后依然保留。
            </p>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading font-extrabold text-2xl text-foreground-950 mb-5 flex items-center gap-2">
            <i className="ri-movie-2-line text-primary-500"></i>
            相关推荐
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
            {related.map((item) => (
              <AnimeCard key={item.id} anime={item} showSummary={false} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm animate-fade-in">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-primary-400"></i>
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}